import React, { useEffect, useRef, useState } from "react";

import dayjs from "dayjs";
import { File, FileText, Paperclip, Upload, X } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { toast } from "~/hooks/use-toast";
import { ensureSalesRequestDocumentsTableExists } from "~/lib/document-upload";
import {
  deleteSalesRequestDocument,
  fetchSalesRequestDocuments,
  uploadSalesRequestDocument,
  uploadTemporaryDocument,
} from "~/services/salesRequestDocumentService";
import { useProfileStore } from "~/store/profileStore";
import { DocumentType, SalesRequestDocument } from "~/types/client";

interface DocumentUploadFormProps {
  salesRequestId?: string;
  isCreating?: boolean;
  onDocumentsChanged?: (documents: SalesRequestDocument[]) => void;
  onUploadStart?: () => void;
  onUploadComplete?: (
    success: boolean,
    documents?: SalesRequestDocument[]
  ) => void;
}

const documentTypeLabels: Record<DocumentType, string> = {
  orden_de_compra: "Orden de Compra",
  certificado_de_calidad: "Certificado de Calidad",
  ficha_tecnica: "Ficha Técnica",
  factura: "Factura",
  nota_de_credito: "Nota de Crédito",
  guia_de_despacho_electronica: "Guía de Despacho Electrónica",
  hes: "HES (Hoja de Entrada de Servicios)",
};

// Document types that carry a reference number + document date (stored in the
// order_number / document_date columns). Drives the extra form fields and badges.
const REFERENCE_NUMBER_TYPES: Partial<
  Record<DocumentType, { fieldLabel: string; placeholder: string; badge: string }>
> = {
  orden_de_compra: {
    fieldLabel: "Número de OC",
    placeholder: "Ingrese el número de la orden de compra",
    badge: "OC",
  },
  guia_de_despacho_electronica: {
    fieldLabel: "Número de Guía",
    placeholder: "Ingrese el número de la guía de despacho",
    badge: "Guía",
  },
  hes: {
    fieldLabel: "Número de HES",
    placeholder: "Ingrese el número de HES",
    badge: "HES",
  },
};

const DocumentUploadForm: React.FC<DocumentUploadFormProps> = ({
  salesRequestId,
  isCreating = false,
  onDocumentsChanged,
  onUploadStart,
  onUploadComplete,
}) => {
  const [documents, setDocuments] = useState<SalesRequestDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [documentType, setDocumentType] =
    useState<DocumentType>("orden_de_compra");
  const [orderNumber, setOrderNumber] = useState<string>("");
  const [documentDate, setDocumentDate] = useState<string>(
    dayjs().format("YYYY-MM-DD")
  );
  const [uploading, setUploading] = useState(false);
  const [hasCheckedTable, setHasCheckedTable] = useState(false);
  const isUploadingRef = useRef(false);
  const [tempDocuments, setTempDocuments] = useState<
    {
      file: File;
      type: DocumentType;
      id: number;
      orderNumber?: string;
      documentDate?: string;
    }[]
  >([]);

  // Get the current user ID from the profile store
  const { id: currentUserId } = useProfileStore();

  // Config for the reference-number field (OC / Guía / HES), or undefined when
  // the selected type is a plain attachment.
  const referenceConfig = REFERENCE_NUMBER_TYPES[documentType];
  const requiresReferenceNumber = Boolean(referenceConfig);

  // Check if the sales_request_documents table exists
  useEffect(() => {
    if (!hasCheckedTable) {
      ensureSalesRequestDocumentsTableExists()
        .then((exists) => {
          if (!exists) {
            toast({
              title: "Error de configuración",
              description:
                "No se encontró la tabla de documentos. Contacte al administrador del sistema para ejecutar las migraciones necesarias.",
              variant: "destructive",
            });
          }
          setHasCheckedTable(true);
        })
        .catch((error) => {
          console.error("Error checking table:", error);
        });
    }
  }, [hasCheckedTable]);

  // Reset order number and document date when switching to a type without a
  // reference number.
  useEffect(() => {
    if (!REFERENCE_NUMBER_TYPES[documentType]) {
      setOrderNumber("");
      setDocumentDate("");
    }
  }, [documentType]);

  // Fetch existing documents (if not in creation mode)
  useEffect(() => {
    if (
      salesRequestId &&
      !isCreating &&
      !isUploadingRef.current &&
      hasCheckedTable
    ) {
      fetchDocuments();
    }
  }, [salesRequestId, isCreating, hasCheckedTable]);

  const fetchDocuments = async () => {
    if (!salesRequestId) return;

    setLoading(true);
    try {
      const fetchedDocuments = await fetchSalesRequestDocuments(salesRequestId);
      setDocuments(fetchedDocuments);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los documentos existentes.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);

    // Create preview for images
    if (selectedFile.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result as string);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setFilePreview(null);
    }
  };

  // Handle document type selection
  const handleDocumentTypeChange = (value: string) => {
    setDocumentType(value as DocumentType);
  };

  // Validate form before submission
  const validateForm = (): boolean => {
    if (!file) {
      toast({
        title: "No hay archivo seleccionado",
        description: "Por favor seleccione un archivo para subir.",
        variant: "destructive",
      });
      return false;
    }

    if (referenceConfig && !orderNumber.trim()) {
      toast({
        title: `${referenceConfig.fieldLabel} requerido`,
        description: `Por favor ingrese el ${referenceConfig.fieldLabel.toLowerCase()}.`,
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  // Handle adding a document (either upload immediately or store temporarily during creation)
  const handleAddDocument = async () => {
    if (!validateForm()) return;

    if (!hasCheckedTable) {
      toast({
        title: "Configuración en progreso",
        description:
          "Espere mientras se completa la configuración del sistema.",
        variant: "destructive",
      });
      return;
    }

    // Prevent duplicate uploads
    if (isUploadingRef.current) {
      return;
    }

    // Signal upload starting
    if (onUploadStart) {
      onUploadStart();
    }

    isUploadingRef.current = true;
    setUploading(true);

    try {
      if (isCreating) {
        // Upload the file immediately even when creating a new sales request
        const result = await uploadTemporaryDocument(
          file!,
          documentType,
          currentUserId || "",
          requiresReferenceNumber ? orderNumber.trim() : undefined,
          requiresReferenceNumber && documentDate ? documentDate : undefined
        );

        if (result) {
          // Add to documents list
          const updatedDocuments = [...documents, result];
          setDocuments(updatedDocuments);

          // Reset form fields
          setFile(null);
          setFilePreview(null);
          if (requiresReferenceNumber) {
            setOrderNumber("");
            setDocumentDate("");
          }

          // Notify parent about document change
          if (onDocumentsChanged) {
            console.log(
              "DocumentUploadForm: Notifying parent of document change",
              updatedDocuments
            );
            // Deep clone the documents to ensure we're passing a new reference
            onDocumentsChanged([...updatedDocuments]);
          }

          // Signal upload completion
          if (onUploadComplete) {
            onUploadComplete(true, updatedDocuments);
          }

          toast({
            title: "Documento subido",
            description:
              "El documento ha sido subido exitosamente y se asociará a la nota de venta al guardar.",
          });
        } else {
          // Upload failed
          if (onUploadComplete) {
            onUploadComplete(false);
          }
        }
      } else if (salesRequestId) {
        // Upload and associate the document with an existing sales request
        const result = await uploadSalesRequestDocument(
          file!,
          salesRequestId,
          documentType,
          currentUserId || "",
          requiresReferenceNumber ? orderNumber.trim() : undefined,
          requiresReferenceNumber && documentDate ? documentDate : undefined
        );

        if (result) {
          // Update documents list and notify parent
          const updatedDocuments = [...documents, result];
          setDocuments(updatedDocuments);

          // Reset form fields
          setFile(null);
          setFilePreview(null);
          if (requiresReferenceNumber) {
            setOrderNumber("");
            setDocumentDate("");
          }

          // Notify parent if needed
          if (onDocumentsChanged) {
            onDocumentsChanged(updatedDocuments);
          }

          // Signal completion
          if (onUploadComplete) {
            onUploadComplete(true, updatedDocuments);
          }
        } else {
          // Upload failed
          if (onUploadComplete) {
            onUploadComplete(false);
          }
        }
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      toast({
        title: "Error",
        description: "No se pudo subir el documento. Intente nuevamente.",
        variant: "destructive",
      });

      if (onUploadComplete) {
        onUploadComplete(false);
      }
    } finally {
      setUploading(false);
      isUploadingRef.current = false;
    }
  };

  // Handle removing a document
  const handleRemoveDocument = async (docId: string) => {
    // If it's a temporary document
    if (docId.startsWith("temp_")) {
      const tempId = parseInt(docId.replace("temp_", ""));
      const updatedTempDocs = tempDocuments.filter((doc) => doc.id !== tempId);
      setTempDocuments(updatedTempDocs);

      // Create updated list for parent
      const updatedDocsList = documents.filter((doc) => doc.id !== docId);
      setDocuments(updatedDocsList);
      if (onDocumentsChanged) {
        onDocumentsChanged(updatedDocsList);
      }
      return;
    }

    // If it's a real document
    const confirmed = window.confirm(
      "¿Estás seguro de que deseas eliminar este documento?"
    );
    if (!confirmed) return;

    try {
      setLoading(true);
      const result = await deleteSalesRequestDocument(docId);
      if (result) {
        const updatedDocuments = documents.filter((doc) => doc.id !== docId);
        setDocuments(updatedDocuments);
        if (onDocumentsChanged) {
          onDocumentsChanged(updatedDocuments);
        }

        toast({
          title: "Documento eliminado",
          description: "El documento ha sido eliminado exitosamente.",
        });
      } else {
        toast({
          title: "Error",
          description: "No se pudo eliminar el documento.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      toast({
        title: "Error",
        description: "Ocurrió un error al intentar eliminar el documento.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to get temporary documents to upload later
  const getTemporaryDocuments = () => {
    return tempDocuments;
  };

  // Get a nice display for file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  // Get icon based on file type
  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/"))
      return (
        <img
          src={filePreview || ""}
          alt="Preview"
          className="h-8 w-8 rounded object-cover"
        />
      );
    if (fileType.includes("pdf"))
      return <FileText className="h-8 w-8 text-red-500" />;
    return <File className="h-8 w-8 text-blue-500" />;
  };

  // Get file icons for existing documents
  const getDocumentIcon = (doc: SalesRequestDocument) => {
    if (doc.fileType?.startsWith("image/")) {
      return (
        <img
          src={doc.fileUrl}
          alt="Preview"
          className="h-8 w-8 rounded object-cover"
          onError={(e) => {
            // If image fails to load, show file icon instead
            e.currentTarget.onerror = null;
            e.currentTarget.src = "/placeholder-image.png";
          }}
        />
      );
    }
    if (doc.fileType?.includes("pdf"))
      return <FileText className="h-8 w-8 text-red-500" />;
    return <File className="h-8 w-8 text-blue-500" />;
  };

  // Open document in new window
  const viewDocument = (url: string) => {
    window.open(url, "_blank");
  };

  // Display loading state while checking table
  if (!hasCheckedTable) {
    return (
      <div className="space-y-4">
        <div className="p-4 text-center">
          <div className="animate-pulse">
            Verificando configuración del sistema...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-2">
          <Label>Tipo de Documento</Label>
          <Select value={documentType} onValueChange={handleDocumentTypeChange}>
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar tipo de documento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="orden_de_compra">Orden de Compra</SelectItem>
              <SelectItem value="certificado_de_calidad">
                Certificado de Calidad
              </SelectItem>
              <SelectItem value="ficha_tecnica">Ficha Técnica</SelectItem>
              <SelectItem value="guia_de_despacho_electronica">
                Guía de Despacho Electrónica
              </SelectItem>
              <SelectItem value="hes">
                HES (Hoja de Entrada de Servicios)
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {referenceConfig && (
          <div className="space-y-2">
            <Label>{referenceConfig.fieldLabel}</Label>
            <Input
              type="text"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              placeholder={referenceConfig.placeholder}
              disabled={uploading || loading}
            />
          </div>
        )}

        {referenceConfig && (
          <div className="space-y-2">
            <Label>Fecha del Documento</Label>
            <Input
              type="date"
              value={documentDate}
              onChange={(e) => setDocumentDate(e.target.value)}
              disabled={uploading || loading}
            />
          </div>
        )}

        <div className="space-y-2">
          <Label>Archivo (máx. 5MB)</Label>
          <div className="flex items-center gap-2">
            <Input
              type="file"
              onChange={handleFileChange}
              className="flex-1"
              disabled={uploading || loading}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleAddDocument}
              disabled={
                !file ||
                uploading ||
                loading ||
                (requiresReferenceNumber && !orderNumber.trim())
              }
            >
              {uploading ? (
                <>
                  <span className="mr-2 animate-spin">⏳</span>
                  Subiendo...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Agregar
                </>
              )}
            </Button>
          </div>

          {filePreview && file?.type.startsWith("image/") && (
            <div className="relative mt-2 h-32 w-32 overflow-hidden rounded border">
              <img
                src={filePreview}
                alt="Vista previa"
                className="h-full w-full object-contain"
              />
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  setFilePreview(null);
                }}
                className="absolute right-1 top-1 rounded-full bg-white p-1 text-gray-700 shadow-sm hover:text-gray-900"
                disabled={uploading || loading}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {file && !file.type.startsWith("image/") && (
            <div className="mt-2 flex items-center gap-2 rounded border p-2">
              <File className="h-5 w-5 text-blue-500" />
              <span className="text-sm">{file.name}</span>
              <span className="text-xs text-gray-500">
                ({formatFileSize(file.size)})
              </span>
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  setFilePreview(null);
                }}
                className="ml-auto text-gray-500 hover:text-gray-700"
                disabled={uploading || loading}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Existing documents list */}
      {loading ? (
        <div className="mt-4 p-4 text-center">
          <div className="animate-pulse">Cargando documentos...</div>
        </div>
      ) : documents.length > 0 || tempDocuments.length > 0 ? (
        <div className="mt-4 rounded-md border p-2">
          <h3 className="mb-2 flex items-center text-sm font-medium">
            <Paperclip className="mr-1 h-4 w-4" />
            Documentos Adjuntos
          </h3>
          <div className="space-y-2">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center gap-2 rounded bg-gray-50 p-2 hover:bg-gray-100"
              >
                {getDocumentIcon(doc)}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center">
                    <span className="mr-2 rounded bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-600">
                      {documentTypeLabels[doc.type as DocumentType]}
                    </span>
                    <span className="truncate text-sm">{doc.fileName}</span>
                  </div>
                  <div className="flex items-center text-xs text-gray-500">
                    <span>{formatFileSize(doc.fileSize)}</span>
                    {REFERENCE_NUMBER_TYPES[doc.type as DocumentType] &&
                      doc.orderNumber && (
                        <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5">
                          {REFERENCE_NUMBER_TYPES[doc.type as DocumentType]!
                            .badge}
                          : {doc.orderNumber}
                        </span>
                      )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => viewDocument(doc.fileUrl)}
                    disabled={uploading || loading}
                  >
                    <FileText className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-700"
                    onClick={() => handleRemoveDocument(doc.id)}
                    disabled={uploading || loading}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DocumentUploadForm;
